import type { ReceiptDraft, ReceiptItem } from "../types/receipt";
import { minorAmount } from "./allocations";
import { formatMinor } from "./money-math";
import { receiptCategoryTotals, signedMinor, startReceiptSplit } from "./receipt-splits";

function balances(receipt: ReceiptDraft) {
  try {
    return receiptCategoryTotals({ ...receipt, categorySplit: startReceiptSplit(receipt, receipt.expense) }).difference === 0;
  } catch { return false; }
}

/** Only existing printed discounts may be marked included; never invent a balancing charge. */
function matchingPrices(receipt: ReceiptDraft, alternatives: ReceiptItem[][]): ReceiptDraft | undefined {
  if (balances(receipt)) return;
  const plan = receipt.categorySplit || startReceiptSplit(receipt, receipt.expense);
  const plans = [plan];
  const printedDiscount = (row: typeof plan.adjustments[number]) => row.kind === "discount" && row.target === "all"
    && (signedMinor(row.amount) ?? 0) < 0;
  if (plan.adjustments.some(row => printedDiscount(row) && !row.included)) {
    plans.push({ ...plan, adjustments: plan.adjustments.map(row =>
      printedDiscount(row) ? { ...row, included: true } : row) });
  }
  const matches = new Map<string, ReceiptDraft>();
  for (const items of alternatives) for (const categorySplit of plans) {
    const candidate = { ...receipt, items, categorySplit };
    if (balances(candidate)) matches.set(JSON.stringify([items.map(item => minorAmount(item.total)), categorySplit.adjustments]), candidate);
  }
  return matches.size === 1 ? [...matches.values()][0] : undefined;
}

/** Recovery for an old scan: propose division only when every cent reconciles exactly. */
function dividedPrices(receipt: ReceiptDraft): ReceiptItem[] | undefined {
  const items: ReceiptItem[] = [];
  for (const item of receipt.items) {
    const cents = minorAmount(item.total), count = Number(item.quantity);
    if (cents === null || !item.quantity.trim() || !Number.isSafeInteger(count) || count <= 0 || cents % count) return;
    items.push({ ...item, total: formatMinor(cents / count) });
  }
  return items;
}

export function receiptLinePriceRepair(receipt: ReceiptDraft): ReceiptDraft | undefined {
  const divided = dividedPrices(receipt);
  // Also recover a discount incorrectly applied twice without changing valid prices.
  return matchingPrices(receipt, divided ? [receipt.items, divided] : [receipt.items]);
}

/** Scanner evidence is copied verbatim, independently of the model's calculated line total. */
export function reconcileScannedPrices(receipt: ReceiptDraft, displayedPrices: unknown[]): ReceiptDraft {
  if (displayedPrices.length !== receipt.items.length) return receipt;
  const lines: ReceiptItem[] = [], units: ReceiptItem[] = [];
  for (const [index, item] of receipt.items.entries()) {
    const price = displayedPrices[index];
    const cents = typeof price === "string" ? minorAmount(price) : null;
    if (cents === null) return receipt;
    lines.push({ ...item, total: formatMinor(cents) });
    const count = Number(item.quantity), total = cents * count;
    if (item.quantity.trim() && count > 0 && Number.isSafeInteger(total)) units.push({ ...item, total: formatMinor(total) });
  }
  const alternatives = [receipt.items, lines];
  if (units.length === receipt.items.length) alternatives.push(units);
  const corrected = matchingPrices(receipt, alternatives);
  return corrected ? { ...corrected, warning: [
    "Item prices were reconciled with the printed final total. Review the line totals and included discounts before saving.",
    receipt.warning].filter(Boolean).join("\n").slice(0, 1800) } : receipt;
}
